/**
 * Throttles a function to only invoke at most once per every `wait` milliseconds.
 * Subsequent invocations during the throttling duration will be ignored or executed at the end of the period based on the `tail` option.
 *
 * Notes:
 * Usual procedures need to be taken to ensure correct this context. See test file for examples.
 * It currently doesn't handle any returns, extend if needed.
 *
 * @param function - The function to throttle.
 * @param wait - The number of milliseconds to wait before allowing another invocation of `fn`.
 * @param tail - A boolean indicating if any throttled invocations should be executed ad the end of the wait period.
 * @returns A throttled version of `fn`. This function takes the same arguments as `fn`.
 *
 * @example
 * // Creating a throttled function that logs to the console, not more than once every 2 seconds
 * const throttledLog = throttle((message) => console.log(message), 2000);
 *
 * // Using the throttled function
 * throttledLog("Hello"); // Immediately logs "Hello"
 * throttledLog("World"); // This call is ignored because it's within 2 seconds of the previous call
 *
 * // Creating a throttled function that logs to the console, not more than once every 2 seconds, and executes the last call at the end of the period
 * const throttledLog2 = throttle((message) => console.log(message), 2000, true);
 *
 * // Using the throttled function with tail option
 * throttledLog("Hello"); // Immediately logs "Hello"
 * throttledLog("Sweden"); // This call is ignored because it's within 2 seconds of the previous call
 * throttledLog("World"); // This call is ignored because it's within 2 seconds of the previous call
 * // After 2 seconds, logs "World" but not sweden
 */
export function throttle<T extends unknown[]>(
  fn: (...args: T) => void,
  wait: number,
  tail: boolean = false,
) {
  let inThrottle = false;
  let lastArgs: T | null = null;

  return (...args: T) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => {
        if (tail && lastArgs) {
          fn(...lastArgs);
          lastArgs = null;
        }
        inThrottle = false;
      }, wait);
    } else if (tail) {
      lastArgs = args;
    }
  };
}
